import { SAVE_SCHEMA_VERSION, createInitialState } from '../state/GameStateStore';
import type { GameState } from '../types';

export interface SaveRepository {
  exists(): boolean;
  load(): GameState | null;
  save(state: Readonly<GameState>): void;
  clear(): void;
}

export const SAVE_KEY = 'vejles-koder-adventure.save.v1';

function isGameState(value: unknown): value is GameState {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<GameState>;
  return (
    candidate.schemaVersion === SAVE_SCHEMA_VERSION &&
    candidate.campaignId === 'vejle-historisk-adventure-demo' &&
    Array.isArray(candidate.inventory) &&
    Array.isArray(candidate.visitedLocations) &&
    typeof candidate.flags === 'object' &&
    typeof candidate.settings === 'object'
  );
}

export class LocalStorageSaveRepository implements SaveRepository {
  constructor(private readonly storage: Storage = localStorage) {}

  exists(): boolean {
    return this.load() !== null;
  }

  load(): GameState | null {
    const serialized = this.storage.getItem(SAVE_KEY);
    if (!serialized) return null;

    try {
      const parsed: unknown = JSON.parse(serialized);
      if (!isGameState(parsed)) throw new Error('Save har et ukendt eller ugyldigt format.');
      const state = { ...createInitialState(), ...parsed };
      // Fjern kun demoens gamle startmadpakke; senere optjente genstande bevares.
      if (state.flags.starterLunchRemoved !== true) {
        state.inventory = state.inventory.filter((item) => item !== 'packedLunch');
        if (state.selectedItemId === 'packedLunch') state.selectedItemId = null;
        state.flags = { ...state.flags, starterLunchRemoved: true };
      }
      return state;
    } catch (error) {
      console.warn('Det gemte spil kunne ikke læses og ignoreres.', error);
      return null;
    }
  }

  save(state: Readonly<GameState>): void {
    this.storage.setItem(SAVE_KEY, JSON.stringify(state));
  }

  clear(): void {
    this.storage.removeItem(SAVE_KEY);
  }
}
